import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
	makeStyles,
	Paper,
	Typography,
	Button,
	IconButton,
	MenuItem,
	TextField,
	Tooltip,
	LinearProgress,
} from "@material-ui/core";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import AddIcon from "@material-ui/icons/Add";
import EventIcon from "@material-ui/icons/Event";
import ScheduleIcon from "@material-ui/icons/Schedule";
import EventBusyIcon from "@material-ui/icons/EventBusy";
import CloseIcon from "@material-ui/icons/Close";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import AppointmentModal from "../../components/AppointmentModal";
import MainContainer from "../../components/MainContainer";
import PageHeader from "../../components/PageHeader";
import StatusChip from "../../components/StatusChip";
import AgendaTimeGrid from "../../components/AgendaTimeGrid";
import { contrastInk } from "../../theme/color";

const useStyles = makeStyles((theme) => ({
	// Calendário e painel do dia lado a lado: antes o painel abria abaixo da
	// grade, que é `flex: 1`, então escolher um dia encolhia o mês inteiro.
	body: {
		flex: 1,
		minHeight: 0,
		display: "flex",
		gap: theme.spacing(2),
		paddingBottom: theme.spacing(3),
		[theme.breakpoints.down("sm")]: { flexDirection: "column" },
	},
	calendarWrap: {
		flex: 1,
		minWidth: 0,
		display: "flex",
		flexDirection: "column",
		overflow: "hidden",
		border: `1px solid ${theme.palette.divider}`,
	},
	// A faixa dos dias da semana era parte do mesmo grid com `gridAutoRows: 1fr`
	// — por isso vinha esticada à altura de uma semana. Agora é uma linha
	// própria, fora da grade.
	weekdayRow: {
		flex: "none",
		display: "grid",
		gridTemplateColumns: "repeat(7, 1fr)",
		borderBottom: `1px solid ${theme.palette.divider}`,
		backgroundColor:
			theme.palette.type === "dark"
				? "rgba(148,163,184,0.06)"
				: theme.palette.grey[50],
	},
	weekday: {
		padding: "8px 8px",
		fontWeight: 600,
		fontSize: 11,
		letterSpacing: "0.04em",
		textAlign: "center",
		textTransform: "uppercase",
		color: theme.palette.text.secondary,
	},
	grid: {
		flex: 1,
		minHeight: 0,
		display: "grid",
		gridTemplateColumns: "repeat(7, 1fr)",
		gridTemplateRows: "repeat(6, minmax(84px, 1fr))",
		gap: 1,
		background: theme.palette.divider,
		overflowY: "auto",
	},
	day: {
		background: theme.palette.background.paper,
		padding: 4,
		display: "flex",
		flexDirection: "column",
		gap: 2,
		cursor: "pointer",
		minWidth: 0,
		border: "2px solid transparent",
		transition: theme.transitions.create(["background-color"]),
		"&:hover": { background: theme.palette.action.hover },
	},
	dayOther: { opacity: 0.45 },
	dayWeekend: {
		background:
			theme.palette.type === "dark"
				? "rgba(148,163,184,0.04)"
				: theme.palette.grey[50],
	},
	daySelected: {
		borderColor: theme.palette.primary.main,
	},
	dayHeader: {
		display: "flex",
		alignItems: "center",
		justifyContent: "flex-end",
		minHeight: 20,
	},
	dayNumber: {
		fontSize: 12,
		fontWeight: 600,
		padding: "0 4px",
		color: theme.palette.text.secondary,
	},
	// Hoje ganha marcador cheio no número, não um fundo quase invisível: era
	// `primary.main + "11"`, ~7% de opacidade, que sumia no modo escuro.
	dayNumberToday: {
		minWidth: 20,
		height: 20,
		padding: "0 6px",
		borderRadius: 10,
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		backgroundColor: theme.palette.primary.main,
		color: theme.palette.primary.contrastText,
	},
	event: {
		fontSize: 11,
		fontWeight: 500,
		padding: "1px 5px",
		borderRadius: 4,
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
		cursor: "pointer",
		flex: "none",
	},
	eventTime: { fontWeight: 700, marginRight: 4, opacity: 0.85 },
	more: {
		fontSize: 10,
		fontWeight: 600,
		color: theme.palette.text.secondary,
		paddingLeft: 4,
		cursor: "pointer",
		"&:hover": { textDecoration: "underline" },
	},
	// Painel lateral do dia
	dayPanel: {
		flex: "none",
		width: 320,
		display: "flex",
		flexDirection: "column",
		border: `1px solid ${theme.palette.divider}`,
		overflow: "hidden",
		[theme.breakpoints.down("sm")]: { width: "auto" },
	},
	dayPanelHeader: {
		flex: "none",
		display: "flex",
		alignItems: "flex-start",
		justifyContent: "space-between",
		gap: theme.spacing(1),
		padding: theme.spacing(1.5, 2),
		borderBottom: `1px solid ${theme.palette.divider}`,
	},
	dayPanelList: {
		flex: 1,
		minHeight: 0,
		overflowY: "auto",
		padding: theme.spacing(1),
	},
	dayPanelItem: {
		display: "flex",
		alignItems: "flex-start",
		gap: theme.spacing(1),
		padding: theme.spacing(1),
		borderRadius: 8,
		cursor: "pointer",
		"&:hover": { background: theme.palette.action.hover },
	},
	dayPanelBody: { flex: 1, minWidth: 0 },
	dayPanelTitle: { fontWeight: 600, fontSize: 13, marginBottom: 2 },
	dayPanelMeta: {
		fontSize: 11,
		color: theme.palette.text.secondary,
		fontVariantNumeric: "tabular-nums",
	},
	dayPanelChips: {
		display: "flex",
		flexWrap: "wrap",
		gap: 4,
		marginTop: 4,
	},
	empty: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		gap: theme.spacing(1),
		padding: theme.spacing(5, 2),
		textAlign: "center",
		color: theme.palette.text.secondary,
	},
	emptyIcon: {
		width: 44,
		height: 44,
		borderRadius: 12,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		color: theme.palette.text.hint,
		backgroundColor:
			theme.palette.type === "dark"
				? "rgba(148,163,184,0.10)"
				: theme.palette.grey[100],
	},
	nav: { display: "flex", alignItems: "center", gap: 4 },
	viewGroup: { display: "flex", gap: 4 },
	filterControl: { minWidth: 150 },
	monthSummary: { color: theme.palette.text.secondary },
}));

const KIND_COLORS = {
	meeting: "#1976d2",
	call: "#7b1fa2",
	task: "#388e3c",
	sprint: "#5e35b1",
	reminder: "#f57c00",
	scheduled_message: "#0288d1",
};
const KIND_LABELS = {
	meeting: "Reunião",
	call: "Ligação",
	task: "Tarefa",
	sprint: "Sprint",
	reminder: "Lembrete",
	scheduled_message: "Mensagem",
};
const STATUS_LABELS = {
	scheduled: "Agendado",
	done: "Concluído",
	canceled: "Cancelado",
	pending: "Pendente",
	sent: "Enviada",
	failed: "Falhou",
};

const MONTH_LABELS = [
	"Janeiro","Fevereiro","Março","Abril","Maio","Junho",
	"Julho","Agosto","Setembro","Outubro","Novembro","Dezembro",
];
const WEEKDAY_LABELS = ["Dom","Seg","Ter","Qua","Qui","Sex","Sáb"];

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const startOfGrid = (d) => {
	const s = startOfMonth(d);
	s.setDate(s.getDate() - s.getDay());
	s.setHours(0, 0, 0, 0);
	return s;
};
const endOfGrid = (d) => {
	const s = startOfGrid(d);
	const e = new Date(s);
	e.setDate(e.getDate() + 42);
	return e;
};
const sameDay = (a, b) =>
	a.getFullYear() === b.getFullYear() &&
	a.getMonth() === b.getMonth() &&
	a.getDate() === b.getDate();

const Agenda = () => {
	const classes = useStyles();
	const [cursor, setCursor] = useState(() => new Date());
	const [view, setView] = useState("month"); // month | week | day
	const [scope, setScope] = useState("mine");
	const [kindFilter, setKindFilter] = useState("");
	const [originFilter, setOriginFilter] = useState(""); // "" | appointment | task | sprint
	const [projectFilter, setProjectFilter] = useState("");
	const [projects, setProjects] = useState([]);
	const [appointments, setAppointments] = useState([]);
	const [scheduledMessages, setScheduledMessages] = useState([]);
	const [loading, setLoading] = useState(false);
	const [selectedDay, setSelectedDay] = useState(null);
	const [modalOpen, setModalOpen] = useState(false);
	const [editing, setEditing] = useState(null);

	// Dias exibidos nas visões de grade. `null` = visão de mês.
	const visibleDays = useMemo(() => {
		if (view === "day") {
			const d = new Date(cursor);
			d.setHours(0, 0, 0, 0);
			return [d];
		}
		if (view === "week") {
			const s = new Date(cursor);
			s.setDate(s.getDate() - s.getDay());
			s.setHours(0, 0, 0, 0);
			return Array.from({ length: 7 }, (_, i) => {
				const d = new Date(s);
				d.setDate(s.getDate() + i);
				return d;
			});
		}
		return null;
	}, [view, cursor]);

	// A janela carregada acompanha o que está na tela: mês usa a grade de 6
	// semanas; semana e dia usam exatamente o período visível.
	const range = useMemo(() => {
		if (visibleDays) {
			const from = new Date(visibleDays[0]);
			const to = new Date(visibleDays[visibleDays.length - 1]);
			to.setHours(23, 59, 59, 999);
			return { from: from.toISOString(), to: to.toISOString() };
		}
		return {
			from: startOfGrid(cursor).toISOString(),
			to: endOfGrid(cursor).toISOString(),
		};
	}, [cursor, visibleDays]);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const params = {
				scope,
				from: range.from,
				to: range.to,
			};
			if (kindFilter) params.kind = kindFilter;
			const [aRes, smRes] = await Promise.all([
				api.get("/appointments", { params }),
				kindFilter && kindFilter !== "scheduled_message"
					? Promise.resolve({ data: { records: [] } })
					: api.get("/scheduled-messages", {
							params: {
								from: range.from,
								to: range.to,
								status: "pending",
							},
					  }),
			]);
			setAppointments(aRes.data.records || []);
			setScheduledMessages(smRes.data.records || []);
		} catch (err) {
			toastError(err);
		} finally {
			setLoading(false);
		}
	}, [range, scope, kindFilter]);

	useEffect(() => {
		load();
	}, [load]);

	useEffect(() => {
		api
			.get("/projects")
			.then((r) => {
				const data = r.data;
				const arr = Array.isArray(data)
					? data
					: data?.records || data?.projects || [];
				setProjects(arr);
			})
			.catch(() => {});
	}, []);

	const allEvents = useMemo(() => {
		const originOf = (x) => {
			const t = x?.externalRef?.type;
			if (t === "task") return { icon: "📌", label: "Tarefa", type: "task" };
			if (t === "sprint") return { icon: "🏁", label: "Sprint", type: "sprint" };
			return { icon: "", label: "Compromisso", type: "appointment" };
		};
		const a = appointments.map((x) => {
			const origin = originOf(x);
			return {
				id: `a-${x.id}`,
				date: new Date(x.startAt),
				endDate: x.endAt ? new Date(x.endAt) : null,
				title: x.title,
				kind: x.kind,
				color: x.color || KIND_COLORS[x.kind] || "#666",
				status: x.status,
				raw: x,
				origin,
				originType: origin.type,
				projectId: x?.externalRef?.projectId || null,
				type: "appointment",
			};
		});
		const m = scheduledMessages.map((x) => ({
			id: `m-${x.id}`,
			date: new Date(x.scheduledFor),
			endDate: null,
			title:
				(x.body || "").slice(0, 40) +
				((x.body || "").length > 40 ? "…" : ""),
			kind: "scheduled_message",
			color: KIND_COLORS.scheduled_message,
			status: x.status,
			raw: x,
			originType: "scheduled_message",
			projectId: null,
			type: "scheduled_message",
		}));
		const merged = [...a, ...m];
		const filtered = merged.filter((ev) => {
			if (originFilter) {
				if (originFilter === "appointment" && ev.originType !== "appointment")
					return false;
				if (originFilter === "task" && ev.originType !== "task") return false;
				if (originFilter === "sprint" && ev.originType !== "sprint")
					return false;
				if (
					originFilter === "scheduled_message" &&
					ev.originType !== "scheduled_message"
				)
					return false;
			}
			if (projectFilter) {
				if (String(ev.projectId || "") !== String(projectFilter)) return false;
			}
			return true;
		});
		return filtered.sort((p, q) => p.date - q.date);
	}, [appointments, scheduledMessages, originFilter, projectFilter]);

	const eventsByDay = useMemo(() => {
		const map = new Map();
		for (const ev of allEvents) {
			const key = ev.date.toDateString();
			if (!map.has(key)) map.set(key, []);
			map.get(key).push(ev);
		}
		return map;
	}, [allEvents]);

	const days = useMemo(() => {
		const result = [];
		const start = startOfGrid(cursor);
		for (let i = 0; i < 42; i += 1) {
			const d = new Date(start);
			d.setDate(start.getDate() + i);
			result.push(d);
		}
		return result;
	}, [cursor]);

	const today = new Date();

	const handleEventClick = (ev) => {
		if (ev.type === "appointment") {
			setEditing(ev.raw);
			setModalOpen(true);
		}
	};

	const handleNewAppointment = () => {
		setEditing(null);
		setModalOpen(true);
	};

	// O passo da navegação acompanha a visão: mês anda de mês, semana de 7
	// dias, dia de 1.
	const step = useCallback(
		(dir) =>
			setCursor((c) => {
				const next = new Date(c);
				if (view === "month") next.setMonth(c.getMonth() + dir, 1);
				else if (view === "week") next.setDate(c.getDate() + 7 * dir);
				else next.setDate(c.getDate() + dir);
				return next;
			}),
		[view]
	);
	const goPrevMonth = useCallback(() => step(-1), [step]);
	const goNextMonth = useCallback(() => step(1), [step]);

	/**
	 * Só compromissos próprios são móveis. Tarefa de projeto e sprint chegam
	 * como espelho de outra entidade — arrastar aqui daria a impressão de ter
	 * reagendado a origem, o que não aconteceria.
	 */
	const canEditEvent = useCallback(
		(ev) => ev.type === "appointment" && ev.originType === "appointment",
		[]
	);

	/** Commit do arraste: otimista, com rollback se o servidor recusar. */
	const handleEventChange = useCallback(async (ev, start, end) => {
		const id = ev.raw?.id;
		if (!id) return;
		let snapshot;
		setAppointments((list) => {
			snapshot = list;
			return list.map((a) =>
				a.id === id
					? { ...a, startAt: start.toISOString(), endAt: end.toISOString() }
					: a
			);
		});
		try {
			await api.put(`/appointments/${id}`, {
				startAt: start.toISOString(),
				endAt: end.toISOString(),
			});
		} catch (err) {
			if (snapshot) setAppointments(snapshot);
			toastError(err);
		}
	}, []);
	const goToday = useCallback(() => {
		const now = new Date();
		setCursor(now);
		setSelectedDay(now);
	}, []);

	// Navegação por teclado. Ignora quando o foco está num campo ou o modal
	// está aberto — senão digitar "t" numa busca pularia o mês.
	useEffect(() => {
		const onKeyDown = (e) => {
			if (modalOpen) return;
			const tag = (e.target?.tagName || "").toLowerCase();
			if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) {
				return;
			}
			if (e.metaKey || e.ctrlKey || e.altKey) return;

			if (e.key === "ArrowLeft") goPrevMonth();
			else if (e.key === "ArrowRight") goNextMonth();
			else if (e.key === "t" || e.key === "T") goToday();
			else if (e.key === "Escape") setSelectedDay(null);
		};
		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [modalOpen, goPrevMonth, goNextMonth, goToday]);

	const dayEvents = selectedDay
		? eventsByDay.get(selectedDay.toDateString()) || []
		: [];

	// Altura da grade medida da janela. De propósito NÃO usa `flex: 1` na
	// árvore do time grid — a versão anterior fazia isso e travava o layout.
	const [gridHeight, setGridHeight] = useState(() =>
		Math.max(320, window.innerHeight - 300)
	);
	useEffect(() => {
		const onResize = () =>
			setGridHeight(Math.max(320, window.innerHeight - 300));
		window.addEventListener("resize", onResize);
		return () => window.removeEventListener("resize", onResize);
	}, []);

	// O título acompanha a visão: mês inteiro, intervalo da semana (cruzando
	// virada de mês quando for o caso) ou o dia por extenso.
	const headerTitle = (() => {
		if (view === "day") {
			return cursor.toLocaleDateString("pt-BR", {
				day: "2-digit",
				month: "long",
				year: "numeric",
			});
		}
		if (view === "week" && visibleDays) {
			const a = visibleDays[0];
			const b = visibleDays[visibleDays.length - 1];
			return a.getMonth() === b.getMonth()
				? `${a.getDate()}–${b.getDate()} de ${MONTH_LABELS[a.getMonth()]} ${a.getFullYear()}`
				: `${a.getDate()} de ${MONTH_LABELS[a.getMonth()]} – ${b.getDate()} de ${MONTH_LABELS[b.getMonth()]} ${b.getFullYear()}`;
		}
		return `${MONTH_LABELS[cursor.getMonth()]} ${cursor.getFullYear()}`;
	})();

	const fmtTime = (d) =>
		d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

	return (
		<MainContainer>
			<PageHeader
				title={headerTitle}
				count={allEvents.length}
				subtitle="Compromissos, tarefas de projeto, sprints e mensagens agendadas no mesmo calendário."
				actions={
					<Button
						variant="contained"
						color="primary"
						startIcon={<AddIcon />}
						onClick={handleNewAppointment}
					>
						Novo compromisso
					</Button>
				}
				filters={
					<>
						<div className={classes.nav}>
							<Tooltip title="Mês anterior (←)">
								<IconButton size="small" onClick={goPrevMonth}>
									<ChevronLeftIcon />
								</IconButton>
							</Tooltip>
							<Button size="small" variant="outlined" onClick={goToday}>
								Hoje
							</Button>
							<Tooltip title="Próximo mês (→)">
								<IconButton size="small" onClick={goNextMonth}>
									<ChevronRightIcon />
								</IconButton>
							</Tooltip>
						</div>
						<div className={classes.viewGroup}>
							{[
								["month", "Mês"],
								["week", "Semana"],
								["day", "Dia"],
							].map(([value, label]) => (
								<Button
									key={value}
									size="small"
									onClick={() => setView(value)}
									variant={view === value ? "contained" : "outlined"}
									color={view === value ? "primary" : "default"}
								>
									{label}
								</Button>
							))}
						</div>
						<TextField
							select
							label="Visão"
							className={classes.filterControl}
							value={scope}
							onChange={(e) => setScope(e.target.value)}
						>
							<MenuItem value="mine">Meus</MenuItem>
							<MenuItem value="all">Equipe</MenuItem>
						</TextField>
						<TextField
							select
							label="Tipo"
							className={classes.filterControl}
							value={kindFilter}
							onChange={(e) => setKindFilter(e.target.value)}
						>
							<MenuItem value="">Todos</MenuItem>
							<MenuItem value="meeting">Reunião</MenuItem>
							<MenuItem value="call">Ligação</MenuItem>
							<MenuItem value="task">Tarefa</MenuItem>
							<MenuItem value="reminder">Lembrete</MenuItem>
							<MenuItem value="scheduled_message">Mensagem agendada</MenuItem>
						</TextField>
						<TextField
							select
							label="Origem"
							className={classes.filterControl}
							value={originFilter}
							onChange={(e) => setOriginFilter(e.target.value)}
						>
							<MenuItem value="">Todas</MenuItem>
							<MenuItem value="appointment">Compromisso</MenuItem>
							<MenuItem value="task">Tarefa de projeto</MenuItem>
							<MenuItem value="sprint">Sprint</MenuItem>
							<MenuItem value="scheduled_message">Mensagem agendada</MenuItem>
						</TextField>
						<TextField
							select
							label="Projeto"
							className={classes.filterControl}
							value={projectFilter}
							onChange={(e) => setProjectFilter(e.target.value)}
						>
							<MenuItem value="">Todos</MenuItem>
							{projects.map((p) => (
								<MenuItem key={p.id} value={p.id}>
									{p.name}
								</MenuItem>
							))}
						</TextField>
					</>
				}
			/>

			{loading && <LinearProgress />}

			<div className={classes.body}>
				<Paper variant="outlined" className={classes.calendarWrap}>
					{visibleDays ? (
						<AgendaTimeGrid
							days={visibleDays}
							events={allEvents}
							height={gridHeight}
							onEventClick={handleEventClick}
							onEventChange={handleEventChange}
							canEditEvent={canEditEvent}
						/>
					) : (
					<>
					<div className={classes.weekdayRow}>
						{WEEKDAY_LABELS.map((w) => (
							<div key={w} className={classes.weekday}>
								{w}
							</div>
						))}
					</div>

					{!loading && allEvents.length === 0 ? (
						<div className={classes.empty}>
							<div className={classes.emptyIcon}>
								<EventBusyIcon />
							</div>
							<Typography variant="subtitle1" color="textPrimary">
								Nenhum compromisso em {MONTH_LABELS[cursor.getMonth()]}
							</Typography>
							<Typography variant="body2">
								Tarefas de projeto, sprints e mensagens agendadas aparecem
								aqui automaticamente. Compromissos você cria por aqui.
							</Typography>
							<Button
								style={{ marginTop: 12 }}
								variant="contained"
								color="primary"
								startIcon={<AddIcon />}
								onClick={handleNewAppointment}
							>
								Novo compromisso
							</Button>
						</div>
					) : (
						<div className={classes.grid}>
							{days.map((d) => {
								const inMonth = d.getMonth() === cursor.getMonth();
								const isToday = sameDay(d, today);
								const isSelected = selectedDay && sameDay(d, selectedDay);
								const isWeekend = d.getDay() === 0 || d.getDay() === 6;
								const evs = eventsByDay.get(d.toDateString()) || [];
								const visible = evs.slice(0, 3);
								const more = evs.length - visible.length;
								return (
									<div
										key={d.toISOString()}
										className={[
											classes.day,
											!inMonth ? classes.dayOther : "",
											isWeekend ? classes.dayWeekend : "",
											isSelected ? classes.daySelected : "",
										]
											.filter(Boolean)
											.join(" ")}
										onClick={() => setSelectedDay(d)}
									>
										<div className={classes.dayHeader}>
											<span
												className={[
													classes.dayNumber,
													isToday ? classes.dayNumberToday : "",
												]
													.filter(Boolean)
													.join(" ")}
											>
												{d.getDate()}
											</span>
										</div>
										{visible.map((ev) => (
											<Tooltip
												key={ev.id}
												title={`${fmtTime(ev.date)} — ${ev.title}`}
											>
												<div
													className={classes.event}
													// Mesma regra dos chips de tag: o fundo é a cor
													// do evento e só a tinta do texto se adapta,
													// senão evento amarelo fica ilegível.
													style={{
														background: ev.color,
														color: contrastInk(ev.color),
													}}
													onClick={(e) => {
														e.stopPropagation();
														handleEventClick(ev);
													}}
												>
													<span className={classes.eventTime}>
														{fmtTime(ev.date)}
													</span>
													{ev.origin?.icon ? `${ev.origin.icon} ` : ""}
													{ev.title}
												</div>
											</Tooltip>
										))}
										{more > 0 && (
											<span
												className={classes.more}
												onClick={(e) => {
													e.stopPropagation();
													setSelectedDay(d);
												}}
											>
												+ {more} mais
											</span>
										)}
									</div>
								);
							})}
						</div>
					)}
					</>
					)}
				</Paper>

				{selectedDay && (
					<Paper variant="outlined" className={classes.dayPanel}>
						<div className={classes.dayPanelHeader}>
							<div>
								<Typography variant="subtitle1" style={{ fontWeight: 600 }}>
									{selectedDay.toLocaleDateString("pt-BR", {
										day: "2-digit",
										month: "long",
									})}
								</Typography>
								<Typography variant="caption" color="textSecondary">
									{/* weekday e year juntos no toLocaleDateString saem como
									    "2026 sábado" em pt-BR; compostos separadamente. */}
									{selectedDay.toLocaleDateString("pt-BR", { weekday: "long" })}
									{", "}
									{selectedDay.getFullYear()}
									{" · "}
									{dayEvents.length}{" "}
									{dayEvents.length === 1 ? "evento" : "eventos"}
								</Typography>
							</div>
							<Tooltip title="Fechar (Esc)">
								<IconButton size="small" onClick={() => setSelectedDay(null)}>
									<CloseIcon fontSize="small" />
								</IconButton>
							</Tooltip>
						</div>

						<div className={classes.dayPanelList}>
							{dayEvents.length === 0 ? (
								<div className={classes.empty}>
									<Typography variant="body2">
										Nada agendado neste dia.
									</Typography>
									<Button
										size="small"
										variant="outlined"
										startIcon={<AddIcon />}
										onClick={handleNewAppointment}
									>
										Agendar
									</Button>
								</div>
							) : (
								dayEvents.map((ev) => (
									<div
										key={ev.id}
										className={classes.dayPanelItem}
										onClick={() => handleEventClick(ev)}
									>
										{ev.type === "scheduled_message" ? (
											<ScheduleIcon fontSize="small" style={{ color: ev.color }} />
										) : (
											<EventIcon fontSize="small" style={{ color: ev.color }} />
										)}
										<div className={classes.dayPanelBody}>
											<div className={classes.dayPanelTitle}>
												{ev.origin?.icon ? `${ev.origin.icon} ` : ""}
												{ev.title}
											</div>
											<div className={classes.dayPanelMeta}>
												{fmtTime(ev.date)}
												{ev.endDate ? ` – ${fmtTime(ev.endDate)}` : ""}
												{ev.origin ? ` · ${ev.origin.label}` : ""}
											</div>
											<div className={classes.dayPanelChips}>
												<StatusChip
													label={KIND_LABELS[ev.kind] || ev.kind}
													color={ev.color}
												/>
												<StatusChip
													status={ev.status}
													label={STATUS_LABELS[ev.status]}
												/>
											</div>
										</div>
									</div>
								))
							)}
						</div>
					</Paper>
				)}
			</div>

			<AppointmentModal
				open={modalOpen}
				onClose={() => {
					setModalOpen(false);
					setEditing(null);
				}}
				appointment={editing}
				defaultDate={selectedDay}
				onSaved={() => {
					setModalOpen(false);
					setEditing(null);
					load();
				}}
				onDeleted={() => {
					setModalOpen(false);
					setEditing(null);
					load();
				}}
			/>
		</MainContainer>
	);
};

export default Agenda;
